import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Bell, Check, X, AlertTriangle, Info, CheckCircle2, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TYPE_STYLES = {
  info: { icon: Info, bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  alert: { icon: AlertTriangle, bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  success: { icon: CheckCircle2, bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

export default function NotificationBell({ token }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notifications`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [token]); // eslint-disable-line

  const markRead = async (id) => {
    await axios.post(`${BACKEND_URL}/api/notifications/${id}/read`, {}, { headers }).catch(() => {});
    fetchNotifications();
  };

  const markAllRead = async () => {
    await axios.post(`${BACKEND_URL}/api/notifications/read-all`, {}, { headers }).catch(() => {});
    fetchNotifications();
  };

  const userId = useMemo(() => {
    try { return JSON.parse(atob(token.split('.')[1])).user_id; } catch { return ''; }
  }, [token]);

  return (
    <div className="relative" data-testid="notification-bell">
      <button onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className="relative p-2 rounded-lg hover:bg-muted/60 transition-colors" data-testid="notification-bell-btn">
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] flex items-center justify-center font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 w-80 max-h-[70vh] bg-card border rounded-xl shadow-xl overflow-hidden" data-testid="notification-panel">
            <div className="p-3 border-b flex items-center justify-between">
              <span className="text-sm font-bold">Notifications</span>
              <div className="flex gap-1">
                {unreadCount > 0 && (
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={markAllRead}>Mark all read</Button>
                )}
                <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-muted/60"><X className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <ScrollArea className="max-h-[55vh]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No notifications</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map(n => {
                    const style = TYPE_STYLES[n.type] || TYPE_STYLES.info;
                    const isRead = (n.read_by || []).includes(userId);
                    const Icon = style.icon;
                    return (
                      <button key={n.id} onClick={() => { if (!isRead) markRead(n.id); if (n.link) window.location.hash = n.link; }}
                        className={`w-full text-left p-3 text-xs transition-colors ${isRead ? 'opacity-60' : 'hover:bg-muted/40'}`}>
                        <div className="flex gap-2">
                          {!isRead && <div className={`w-1.5 h-1.5 rounded-full ${style.dot} mt-1.5 shrink-0`} />}
                          <div className="flex-1 min-w-0">
                            <span className="font-medium block">{n.title}</span>
                            <span className="text-muted-foreground block mt-0.5">{n.message}</span>
                            <span className="text-[9px] text-muted-foreground/60 mt-1 block">
                              {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </>
      )}
    </div>
  );
}
